<template>
  <div class="container">
    <!-- 页面标题和菜单栏 -->
    <div class="container01">
      <h1 class="container01_h1">商品管理</h1>
      <!-- 菜单栏按钮 -->
      <el-row :gutter="20">
        <el-col :span="24" class="button-col">
          <div class="container01_button">
            <el-button @click="$router.back()">返回上一级</el-button>
            <el-button type="primary" @click="handleAddMerchandise">新增商品</el-button>
            <el-button type="primary" @click="handleBatchImport">批量导入</el-button>
            <el-button type="danger" @click="handleBatchDelete"
              :disabled="selectedMerchandiseIds.length === 0">删除选中商品</el-button>
            <!-- 商品专属操作按钮（示例，可根据需求扩展） -->
            <el-button type="primary" @click="handleStockWarning">库存预警</el-button>
            <el-button type="primary" @click="handleCategoryManage" style="margin-right: 10px;">分类管理</el-button>
          </div>
        </el-col>
      </el-row>
      <!-- 搜索栏 -->
      <el-row :gutter="20">
        <el-col :span="24" class="search-col">
          <el-input v-model="searchKeyword" placeholder="请输入商品名称/规格，支持模糊搜索" clearable class="container01_search"
            @keyup.enter="handleSearch"></el-input>
          <el-button type="primary" @click="handleSearch" style="margin-top: 20px;">搜索</el-button>
        </el-col>
      </el-row>
    </div>
    <!-- 信息表格 -->
    <div class="container02">
      <div>
        <el-table :data="paginatedData" border style="width: 100%" height="calc(100vh - 200px)" v-loading="loading"
          element-loading-text="加载中" @selection-change="handleSelectionChange">
          <el-table-column type="selection" width="55" />
          <el-table-column prop="merchandise_id" label="商品ID" width="100" />
          <el-table-column prop="name" label="商品名称" min-width="5%" />
          <el-table-column prop="specification" label="商品规格" min-width="5%" />
          <el-table-column prop="category" label="商品分类" min-width="10%" />
          <el-table-column label="操作" min-width="3%">
            <template #default="scope">
              <el-button type="text" @click="handleViewDetail(scope.row.merchandise_id)">详情</el-button>
              <el-button type="text" @click="handleDelete(scope.row.merchandise_id)"
                style="color: rgb(250,182,182);">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
        <!-- 分页按钮 -->
        <div class="pagination">
          <button @click="prevPage" :disabled="currentPage <= 1" class="page-button">上一页</button>
          <span class="page-info">当前第 {{ currentPage }} 页 / 共 {{ totalPages }} 页 - 共 {{ totalCount }} 条数据</span>
          <button @click="nextPage" :disabled="currentPage >= totalPages" class="page-button">下一页</button>
        </div>
      </div>
    </div>
    <!-- 新增商品弹窗 -->
    <el-dialog title="新增商品" v-model="addDialogVisible">
      <el-form :model="newMerchandise" ref="form">
        <el-form-item label="商品名称" required>
          <el-input v-model="newMerchandise.name"></el-input>
        </el-form-item>
        <el-form-item label="商品规格">
          <el-input v-model="newMerchandise.specification" placeholder="选填（如：330ml/瓶）"></el-input>
        </el-form-item>
        <el-form-item label="商品分类">
          <el-input v-model="newMerchandise.category" placeholder="选填（如：饮料/零食）"></el-input>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="addDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveMerchandise">保存</el-button>
      </template>
    </el-dialog>
    <!-- 批量导入商品弹窗 -->
    <el-dialog title="批量导入商品" v-model="importDialogVisible">
      <div class="import-area">
        <input type="file" ref="fileInput" accept=".xlsx,.json" @change="handleFile">
        <button @click="triggerFile">选择Excel或JSON文件</button>
        <p v-if="fileName">{{ fileName }}</p>
      </div>
      <template #footer>
        <el-button @click="importDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="doImport" :disabled="!fileName">导入</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import request from '@/utils/request';
// 导入商品模块API（需确保 @/api/merchandises.js 已按之前的设计实现）
import merchandiseAPI from '@/api/merchandises';

export default {
  data() {
    return {
      // 商品列表数据
      merchandises: [],
      // 搜索关键词
      searchKeyword: '',
      loading: false,
      // 分页参数
      currentPage: 1,
      pageSize: 20,
      totalCount: 0,
      // 弹窗控制
      addDialogVisible: false,
      importDialogVisible: false,
      // 新增商品表单数据
      newMerchandise: { name: '', specification: '', category: '' },
      // 文件导入相关
      fileName: '',
      file: null,
      // 选中的商品ID（批量操作用）
      selectedMerchandiseIds: []
    }
  },
  created() {
    this.fetchMerchandisesData(); // 初始化加载商品数据
  },
  computed: {
    totalPages() {
      return Math.ceil(this.totalCount / this.pageSize) || 1;
    },
    paginatedData() {
      return this.merchandises;
    }
  },
  methods: {
    // 加载商品列表（含分页、搜索）
    async fetchMerchandisesData() {
      this.loading = true;
      try {
        const params = {
          keyword: this.searchKeyword,
          page: this.currentPage,
          per_page: this.pageSize
        };
        // 调用商品API的“列表查询”方法
        const originData = await merchandiseAPI.inquireMerchandises(params);
        const merchandiseList = Array.isArray(originData.merchandises) ? originData.merchandises : [];
        this.merchandises = merchandiseList;
        this.totalCount = originData.total || 0;
        this.currentPage = originData.page || 1;
      } catch (error) {
        console.error('获取商品数据失败：', error);
        this.$message.error('加载商品列表失败，请重试');
      } finally {
        this.loading = false;
      }
    },
    // 新增商品-提交表单
    async saveMerchandise() {
      if (!this.newMerchandise.name.trim()) {
        this.$message.warning('商品名称不能为空');
        return;
      }
      try {
        // 调用商品API的“新增”方法
        await merchandiseAPI.createMerchandise(this.newMerchandise);
        this.$message.success('商品新增成功');
        this.addDialogVisible = false;
        this.newMerchandise = { name: '', specification: '', category: '' };
        this.fetchMerchandisesData(); // 刷新列表
      } catch (err) {
        console.error('新增商品失败：', err);
        this.$message.error('新增商品失败，请重试');
      }
    },
    // 批量导入-触发文件选择
    triggerFile() {
      this.$refs.fileInput.value = '';
      this.$refs.fileInput.click();
    },
    // 批量导入-处理选中的文件
    handleFile(e) {
      const file = e.target.files[0];
      if (file) {
        const allowedTypes = ['application/json', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
        const allowedExtensions = ['.json', '.xlsx'];
        const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
        if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
          this.$message.error('仅支持 JSON 或 XLSX 格式的文件');
          this.file = null;
          this.fileName = '';
          return;
        }
        this.file = file;
        this.fileName = file.name;
      }
    },
    // 批量导入-提交文件到后端
    async doImport() {
      const formData = new FormData();
      formData.append('file', this.file);
      try {
        // 显示加载状态
        this.loading = true;

        // 调用商品批量导入接口
        await request.post('/api/merchandises/batch', formData, {
          timeout: 30000, // 30秒超时
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        this.$message.success('商品批量导入成功');
        this.importDialogVisible = false;
        this.fileName = '';
        this.fetchMerchandisesData(); // 刷新列表
        this.$refs.fileInput.value = '';

      } catch (err) {
        console.error('导入失败详情：', err);

        // 更详细的错误信息处理
        let errorMessage = '导入失败：';

        if (err.response) {
          // 服务器返回了错误状态码
          const status = err.response.status;
          const data = err.response.data;

          switch (status) {
            case 400:
              errorMessage += data.message || '请求参数错误，请检查文件格式和数据';
              break;
            case 413:
              errorMessage += '文件太大，请压缩后重新上传';
              break;
            case 415:
              errorMessage += '不支持的文件类型';
              break;
            case 500:
              errorMessage += data.message || '服务器内部错误，请联系管理员';
              break;
            default:
              errorMessage += data.message || `服务器错误 (${status})`;
          }
        } else if (err.request) {
          // 请求发送但无响应
          errorMessage += '网络错误，无法连接到服务器，请检查网络连接';
        } else {
          // 其他错误
          errorMessage += err.message || '未知错误';
        }

        this.$message.error(errorMessage);
      } finally {
        this.loading = false;
      }
    },
    // 分页-上一页
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.fetchMerchandisesData();
      }
    },
    // 分页-下一页
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.fetchMerchandisesData();
      }
    },
    // 搜索触发
    handleSearch() {
      this.currentPage = 1;
      this.fetchMerchandisesData();
    },
    // 打开“新增商品”弹窗
    handleAddMerchandise() {
      this.addDialogVisible = true;
    },
    // 打开“批量导入”弹窗
    handleBatchImport() {
      this.importDialogVisible = true;
    },
    // 打开“商品详情”页
    handleViewDetail(merchandiseId) {
      this.$router.push(`/merchandises/detail/${merchandiseId}`);
    },
    // 商品专属功能（示例，需后端配合扩展）
    handleStockWarning() {
      this.$message.info('库存预警功能待实现');
    },
    handleCategoryManage() {
      this.$message.info('分类管理功能待实现');
    },
    // 表格行选中事件（收集选中的商品ID）
    handleSelectionChange(selectedRows) {
      this.selectedMerchandiseIds = selectedRows.map(row => row.merchandise_id);
    },
    // 单个商品删除
    async handleDelete(merchandiseId) {
      try {
        await this.$confirm('确定要删除该商品吗？删除后数据不可恢复！', '删除确认', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        });
        // 调用商品API的“删除单个”方法
        await merchandiseAPI.deleteMerchandise(merchandiseId);
        this.$message.success('商品删除成功');
        this.fetchMerchandisesData(); // 刷新列表
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除失败：', error);
          this.$message.error('删除商品失败，请重试');
        }
      }
    },
    // 批量删除商品
    async handleBatchDelete() {
      try {
        if (this.selectedMerchandiseIds.length === 0) {
          this.$message.warning('请先选中要删除的商品');
          return;
        }
        await this.$confirm(`确定要删除选中的 ${this.selectedMerchandiseIds.length} 个商品吗？删除后数据不可恢复！`, '批量删除确认', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        });
        // 调用商品API的“批量删除”方法
        await merchandiseAPI.batchDeleteMerchandises(this.selectedMerchandiseIds);
        this.$message.success('批量删除成功');
        this.fetchMerchandisesData(); // 刷新列表
        this.selectedMerchandiseIds = []; // 清空选中状态
      } catch (error) {
        if (error !== 'cancel') {
          console.error('批量删除失败：', error);
          this.$message.error('批量删除商品失败，请重试');
        }
      }
    },
  }
}
</script>

<style scoped>
.container01_search {
  width: 80%;
  margin-right: 10px;
  margin-top: 20px;
}

.container02 {
  width: 90%;
  margin-top: 20px;
  margin-left: auto;
  margin-right: auto;
}

.page-button {
  margin-left: 10px;
  margin-right: 10px;
}

.el-dialog--default {
  width: 20%;
  padding: 20px;
}

.import-area {
  padding: 20px 0;
}

.import-area input {
  display: none;
}

.import-area button {
  padding: 8px 16px;
  background-color: #409eff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.import-area button:hover {
  background-color: #66b1ff;
}

.import-area p {
  margin-top: 10px;
  color: #606266;
}
</style>